import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useNavigate } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LayoutDashboard, Users, Building, Shield, Settings, FileText, 
  BarChart3, TrendingUp, AlertCircle, Target, UserPlus, ChevronRight,
  CheckCircle, Clock, Search, Filter, Download, Eye, Edit, Trash2,
  Plus, RefreshCw, Calendar, Briefcase, GraduationCap, Wrench, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier colors
const TIER_COLORS = {
  emerging: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Emerging' },
  developing: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Developing' },
  internship_ready: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Internship Ready' },
  hire_ready: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Hire Ready' }
};

// KPI Card Component
const KPICard = ({ icon: Icon, label, value, subValue, color = 'blue', trend }) => (
  <GlassCard className="p-5">
    <div className="flex items-start justify-between">
      <div className={`w-12 h-12 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}-400`} />
      </div>
      {trend && (
        <span className={`text-sm font-medium ${trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <div className="mt-4">
      <p className="text-3xl font-bold text-white">{value}</p>
      <p className="text-sm text-slate-300">{label}</p>
      {subValue && <p className="text-xs text-slate-300 mt-1">{subValue}</p>}
    </div>
  </GlassCard>
);

// Readiness Distribution Chart
const ReadinessChart = ({ distribution }) => {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  
  return (
    <div className="space-y-3">
      {Object.entries(distribution).map(([tier, count]) => {
        const tierConfig = TIER_COLORS[tier] || TIER_COLORS.emerging;
        const percent = total > 0 ? (count / total) * 100 : 0;
        
        return (
          <div key={tier}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-sm ${tierConfig.text}`}>{tierConfig.label}</span>
              <span className="text-sm text-slate-300">{count} ({percent.toFixed(1)}%)</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <motion.div
                className={`h-full ${tierConfig.bg.replace('/20', '')}`}
                initial={{ width: 0 }}
                animate={{ width: `${percent}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Roster Table Row
const RosterRow = ({ student, onView }) => {
  const tierConfig = TIER_COLORS[student.cri_tier] || TIER_COLORS.emerging;
  
  return (
    <tr className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold">
            {student.full_name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div>
            <p className="text-white font-medium">{student.full_name}</p>
            <p className="text-slate-300 text-xs">{student.email}</p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <span className="text-slate-300">{student.pathway?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Undecided'}</span>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-white">{student.cri_score}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs ${tierConfig.bg} ${tierConfig.text}`}>
            {tierConfig.label}
          </span>
        </div>
      </td>
      <td className="py-4 px-4">
        <span className="text-slate-300">{student.ats_score || 0}</span>
      </td>
      <td className="py-4 px-4">
        {student.risk_flags?.length > 0 ? (
          <div className="flex gap-1">
            {student.risk_flags.map((flag, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400">
                {flag.replace('_', ' ')}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-emerald-400 text-sm">On Track</span>
        )}
      </td>
      <td className="py-4 px-4">
        <Button size="sm" variant="ghost" onClick={() => onView(student)} className="text-blue-400 hover:text-blue-300">
          <Eye className="w-4 h-4 mr-1" /> View
        </Button>
      </td>
    </tr>
  );
};

// Organization Card
const OrgCard = ({ org, onManage }) => (
  <GlassCard className="p-5 hover:border-blue-500/30 transition-all cursor-pointer" onClick={() => onManage(org)}>
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl ${
          org.org_type === 'school' ? 'bg-blue-500/20' :
          org.org_type === 'university' ? 'bg-purple-500/20' :
          org.org_type === 'workforce' ? 'bg-amber-500/20' :
          org.org_type === 'employer' ? 'bg-emerald-500/20' :
          'bg-slate-500/20'
        } flex items-center justify-center`}>
          {org.org_type === 'school' && <GraduationCap className="w-6 h-6 text-blue-400" />}
          {org.org_type === 'university' && <Building className="w-6 h-6 text-purple-400" />}
          {org.org_type === 'workforce' && <Briefcase className="w-6 h-6 text-amber-400" />}
          {org.org_type === 'employer' && <Building className="w-6 h-6 text-emerald-400" />}
          {org.org_type === 'district' && <Building className="w-6 h-6 text-slate-300" />}
        </div>
        <div>
          <h3 className="text-white font-semibold">{org.name}</h3>
          <p className="text-slate-300 text-sm capitalize">{org.org_type}</p>
        </div>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs ${org.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
        {org.status}
      </span>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-slate-300 text-xs">Members</p>
        <p className="text-white font-semibold">{org.member_count || 0}</p>
      </div>
      <div>
        <p className="text-slate-300 text-xs">Created</p>
        <p className="text-white font-semibold">{new Date(org.created_at).toLocaleDateString()}</p>
      </div>
    </div>
  </GlassCard>
);

// Create Organization Modal
const CreateOrgModal = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    org_type: 'school',
    contact_email: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onCreate(formData);
      onClose();
      setFormData({ name: '', org_type: 'school', contact_email: '' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#0a1729]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card p-6 w-full max-w-md"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Create Organization</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-slate-300">Organization Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="bg-slate-800 border-slate-700 text-white mt-1"
              required
            />
          </div>
          <div>
            <Label className="text-slate-300">Organization Type</Label>
            <select
              value={formData.org_type}
              onChange={(e) => setFormData(prev => ({ ...prev, org_type: e.target.value }))}
              className="w-full mt-1 p-2 rounded-lg bg-slate-800 border border-slate-700 text-white"
            >
              <option value="school">School (K-12)</option>
              <option value="district">School District</option>
              <option value="university">University</option>
              <option value="workforce">Workforce Program</option>
              <option value="employer">Employer</option>
            </select>
          </div>
          <div>
            <Label className="text-slate-300">Contact Email</Label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-slate-700">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700">
              {loading ? <LoadingSpinner size="sm" /> : 'Create Organization'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Main Enterprise Dashboard
export default function EnterpriseDashboard() {
  const { user, getAuthHeader } = useAuth();
  const { branding } = useTenant();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [kpis, setKpis] = useState(null);
  const [roster, setRoster] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [addStudentData, setAddStudentData] = useState({ full_name: '', email: '', password: 'student123' });
  const [addingStudent, setAddingStudent] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [enterpriseContext, setEnterpriseContext] = useState(null);
  const [dpaStatus, setDpaStatus] = useState(null);
  const [showDpaSigning, setShowDpaSigning] = useState(false);
  const [dpaSignature, setDpaSignature] = useState('');
  const [dpaTitle, setDpaTitle] = useState('');
  const [dpaTemplate, setDpaTemplate] = useState(null);
  const [signingDpa, setSigningDpa] = useState(false);

  useEffect(() => {
    fetchEnterpriseContext();
  }, []);

  const fetchEnterpriseContext = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/me`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setEnterpriseContext(response.data);
      
      if (!response.data.is_enterprise) {
        toast.error('Enterprise access required');
        navigate('/app/dashboard');
        return;
      }
      
      // Fetch data based on role
      await Promise.all([
        fetchKPIs(response.data.org_id),
        fetchRoster(response.data.org_id),
        response.data.role === 'super_admin' && fetchOrganizations()
      ]);

      // Check DPA status
      try {
        const dpaRes = await axios.get(`${API}/compliance/dpa-status`, { ...getAuthHeader(), withCredentials: true });
        setDpaStatus(dpaRes.data);
      } catch { setDpaStatus({ dpa_signed: false }); }
    } catch (error) {
      console.error('Failed to fetch enterprise context:', error);
      toast.error('Failed to load enterprise dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchKPIs = async (orgId) => {
    try {
      const params = orgId ? `?org_id=${orgId}` : '';
      const response = await axios.get(`${API}/enterprise/kpis${params}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setKpis(response.data.kpis);
    } catch (error) {
      console.error('Failed to fetch KPIs:', error);
    }
  };

  const fetchRoster = async (orgId) => {
    try {
      const params = orgId ? `?org_id=${orgId}` : '';
      const response = await axios.get(`${API}/enterprise/roster${params}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setRoster(response.data.roster || []);
    } catch (error) {
      console.error('Failed to fetch roster:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/organizations`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const handleCreateOrg = async (orgData) => {
    try {
      await axios.post(`${API}/enterprise/organizations`, orgData, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Organization created successfully');
      fetchOrganizations();
    } catch (error) {
      showFriendlyError(error, { fallback: 'Failed to create organization' });
    }
  };

  const handleRecalculateCRI = async () => {
    try {
      const orgId = enterpriseContext?.org_id;
      if (!orgId && enterpriseContext?.role !== 'super_admin') {
        toast.error('No organization selected');
        return;
      }
      
      await axios.post(`${API}/enterprise/scores/recalculate`, 
        { org_id: orgId || organizations[0]?.id },
        { ...getAuthHeader(), withCredentials: true }
      );
      toast.success('CRI scores recalculated');
      fetchRoster(orgId);
    } catch (error) {
      toast.error('Failed to recalculate scores');
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setAddingStudent(true);
    try {
      await axios.post(`${API}/enterprise/add-student`, addStudentData, {
        ...getAuthHeader(),
        withCredentials: true
      });
      const tenantSlug = localStorage.getItem('tenant_slug') || '';
      const loginUrl = tenantSlug
        ? `${window.location.origin}/login?tenant=${tenantSlug}`
        : `${window.location.origin}/login`;
      toast.success(
        <div>
          <p className="font-medium">{addStudentData.full_name} added!</p>
          <p className="text-xs mt-1">Email: {addStudentData.email}</p>
          <p className="text-xs">Password: {addStudentData.password}</p>
          <a href={loginUrl} target="_blank" rel="noopener noreferrer" className="text-xs underline mt-1 block">Login Link</a>
        </div>,
        { duration: 8000 }
      );
      setShowAddStudent(false);
      setAddStudentData({ full_name: '', email: '', password: 'student123' });
      fetchRoster(enterpriseContext?.org_id);
    } catch (error) {
      showFriendlyError(error, { fallback: 'Failed to add student' });
    } finally {
      setAddingStudent(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setBulkUploading(true);
    setBulkResult(null);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/enterprise/bulk-add-students`, formData, {
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setBulkResult(response.data);
      if (response.data.created > 0) {
        toast.success(`${response.data.created} students imported successfully!`);
        fetchRoster(enterpriseContext?.org_id);
      }
      if (response.data.skipped > 0) {
        toast.info(`${response.data.skipped} rows skipped (duplicates or missing data)`);
      }
    } catch (error) {
      showFriendlyError(error, { fallback: 'Bulk import failed' });
    } finally {
      setBulkUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const csv = 'full_name,email,password\nJohn Doe,john@example.com,student123\nJane Smith,jane@example.com,student123';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const isSuperAdmin = enterpriseContext?.role === 'super_admin';

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader 
        title={isSuperAdmin ? "Super Admin Dashboard" : (branding?.is_white_label ? `${branding.app_name} Dashboard` : "Enterprise Dashboard")}
        description={isSuperAdmin ? "Manage all organizations and users" : `Managing ${enterpriseContext?.org_name || 'your organization'}`}
      />

      {/* Role Badge */}
      <div className="flex items-center gap-4 mb-6">
        <span className="px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2"
          style={branding?.is_white_label ? {
            background: `${branding.primary_color}20`,
            color: branding.primary_color,
          } : {
            background: 'rgb(168 85 247 / 0.2)',
            color: 'rgb(192 132 252)',
          }}
        >
          <Shield className="w-4 h-4" />
          {enterpriseContext?.role?.replace('_', ' ').toUpperCase()}
        </span>
        {enterpriseContext?.org_name && (
          <span className="px-3 py-1 rounded-full text-sm"
            style={branding?.is_white_label ? {
              background: `${branding.secondary_color || branding.primary_color}20`,
              color: branding.secondary_color || branding.primary_color,
            } : {
              background: 'rgb(59 130 246 / 0.2)',
              color: 'rgb(96 165 250)',
            }}
          >
            {enterpriseContext.org_name}
          </span>
        )}
      </div>

      {/* DPA Banner */}
      {dpaStatus && !dpaStatus.dpa_signed && (
        <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="dpa-banner">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-amber-400 shrink-0" />
            <div className="flex-1">
              <h3 className="text-white font-semibold">Sign Your Data Processing Agreement</h3>
              <p className="text-sm text-amber-200/70">Before adding students, your organization must sign our Data Processing Agreement to comply with FERPA requirements.</p>
            </div>
            <Button size="sm" onClick={async () => {
              try {
                const res = await axios.get(`${API}/compliance/document-template/school_dpa`);
                setDpaTemplate(res.data.document);
                setShowDpaSigning(true);
              } catch { toast.error('Failed to load DPA'); }
            }} data-testid="sign-dpa-btn">
              Sign Now
            </Button>
          </div>
        </div>
      )}

      {/* DPA Signing Modal */}
      {showDpaSigning && dpaTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" data-testid="dpa-signing-modal">
            <h3 className="text-lg font-semibold text-white mb-4">{dpaTemplate.title}</h3>
            <div className="max-h-[300px] overflow-y-auto bg-[#0a1729] rounded-lg border border-slate-700 p-5 space-y-4 text-sm mb-4">
              {dpaTemplate.sections?.map((sec, i) => (
                <div key={i}>
                  <h4 className="font-semibold text-white mb-1">{sec.heading}</h4>
                  <p className="text-slate-300 whitespace-pre-line leading-relaxed">{sec.content}</p>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <Input value={dpaSignature} onChange={(e) => setDpaSignature(e.target.value)} placeholder="Your full legal name" data-testid="dpa-signature-input" />
              <Input value={dpaTitle} onChange={(e) => setDpaTitle(e.target.value)} placeholder="Your title (e.g., Principal, Director)" data-testid="dpa-title-input" />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowDpaSigning(false)} className="flex-1">Cancel</Button>
                <Button disabled={!dpaSignature.trim() || signingDpa} className="flex-1" data-testid="dpa-submit-btn"
                  onClick={async () => {
                    setSigningDpa(true);
                    try {
                      await axios.post(`${API}/compliance/school-dpa-sign`, {
                        typed_signature: dpaSignature, org_title: dpaTitle
                      }, { ...getAuthHeader(), withCredentials: true });
                      setDpaStatus({ dpa_signed: true, dpa_signed_at: new Date().toISOString(), dpa_signer_name: dpaSignature });
                      setShowDpaSigning(false);
                      toast.success('DPA signed successfully!');
                    } catch (err) { showFriendlyError(err, { fallback: 'Failed to sign DPA' }); }
                    finally { setSigningDpa(false); }
                  }}>
                  {signingDpa ? 'Signing...' : 'Sign DPA'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-800/50 p-1 mb-6">
          <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600"
            style={branding?.is_white_label ? { '--tw-bg-opacity': 1 } : undefined}
            data-brand-color={branding?.is_white_label ? branding.primary_color : undefined}
          >
            <LayoutDashboard className="w-4 h-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="roster" className="data-[state=active]:bg-blue-600">
            <Users className="w-4 h-4 mr-2" /> Roster
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="organizations" className="data-[state=active]:bg-blue-600">
              <Building className="w-4 h-4 mr-2" /> Organizations
            </TabsTrigger>
          )}
          <TabsTrigger value="reports" className="data-[state=active]:bg-blue-600">
            <BarChart3 className="w-4 h-4 mr-2" /> Reports
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          {/* KPI Cards */}
          {kpis && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <KPICard 
                icon={Users} 
                label="Active Users" 
                value={kpis.active_users}
                subValue={`of ${kpis.total_users} total`}
                color="blue"
                trend={kpis.growth_percent}
              />
              <KPICard 
                icon={Target} 
                label="Avg CRI Score" 
                value={kpis.avg_cri_score}
                subValue="Career Readiness Index"
                color="emerald"
              />
              <KPICard 
                icon={FileText} 
                label="Avg ATS Score" 
                value={kpis.avg_ats_score}
                subValue="Resume Health"
                color="purple"
              />
              <KPICard 
                icon={AlertCircle} 
                label="At-Risk" 
                value={kpis.at_risk_count}
                subValue="Need intervention"
                color="red"
              />
            </div>
          )}

          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Readiness Distribution */}
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                Readiness Distribution
              </h3>
              <ReadinessChart distribution={{
                emerging: roster.filter(s => s.cri_tier === 'emerging').length,
                developing: roster.filter(s => s.cri_tier === 'developing').length,
                internship_ready: roster.filter(s => s.cri_tier === 'internship_ready').length,
                hire_ready: roster.filter(s => s.cri_tier === 'hire_ready').length
              }} />
            </GlassCard>

            {/* Pathway Distribution */}
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                Pathway Adoption
              </h3>
              <div className="space-y-3">
                {['college', 'trade', 'military', 'entrepreneurship', 'workforce', 'undecided'].map(pathway => {
                  const count = roster.filter(s => s.pathway === pathway).length;
                  const percent = roster.length > 0 ? (count / roster.length) * 100 : 0;
                  const colors = {
                    college: 'bg-blue-500',
                    trade: 'bg-amber-500',
                    military: 'bg-emerald-500',
                    entrepreneurship: 'bg-purple-500',
                    workforce: 'bg-rose-500',
                    undecided: 'bg-slate-500'
                  };
                  return (
                    <div key={pathway}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-300 capitalize">{pathway}</span>
                        <span className="text-sm text-slate-300">{count}</span>
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full ${colors[pathway]}`} style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </GlassCard>
          </div>

          {/* Quick Actions */}
          <GlassCard className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleRecalculateCRI} className="bg-blue-600 hover:bg-blue-700">
                <RefreshCw className="w-4 h-4 mr-2" /> Recalculate CRI Scores
              </Button>
              <Button onClick={() => navigate('/enterprise/interventions')} variant="outline" className="border-slate-600">
                <AlertCircle className="w-4 h-4 mr-2" /> View Interventions
              </Button>
              <Button onClick={() => {}} variant="outline" className="border-slate-600">
                <Download className="w-4 h-4 mr-2" /> Export Report
              </Button>
              {isSuperAdmin && (
                <Button onClick={() => setShowCreateOrg(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" /> Create Organization
                </Button>
              )}
            </div>
          </GlassCard>
        </TabsContent>

        {/* Roster Tab */}
        <TabsContent value="roster">
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Student Roster</h3>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                  <Input placeholder="Search students..." className="pl-9 bg-slate-800 border-slate-700 text-white w-64" />
                </div>
                <Button
                  onClick={() => setShowAddStudent(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  style={branding?.is_white_label ? { background: branding.primary_color } : undefined}
                  data-testid="add-student-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" /> Add Student
                </Button>
                <Button
                  onClick={() => setShowBulkImport(!showBulkImport)}
                  variant="outline"
                  className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10"
                  style={branding?.is_white_label ? { borderColor: branding.secondary_color || branding.primary_color, color: branding.secondary_color || branding.primary_color } : undefined}
                  data-testid="bulk-import-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Bulk Import
                </Button>
              </div>
            </div>

            {/* Add Student Modal */}
            {showAddStudent && (
              <div className="mb-6 p-6 bg-slate-800/80 border border-slate-700 rounded-xl">
                <h4 className="text-white font-semibold mb-4">Add New Student</h4>
                <form onSubmit={handleAddStudent} className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-300 text-sm">Full Name *</Label>
                    <Input
                      placeholder="Student name"
                      value={addStudentData.full_name}
                      onChange={(e) => setAddStudentData({ ...addStudentData, full_name: e.target.value })}
                      required
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="add-student-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Email *</Label>
                    <Input
                      type="email"
                      placeholder="student@email.com"
                      value={addStudentData.email}
                      onChange={(e) => setAddStudentData({ ...addStudentData, email: e.target.value })}
                      required
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="add-student-email"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Temp Password</Label>
                    <Input
                      placeholder="student123"
                      value={addStudentData.password}
                      onChange={(e) => setAddStudentData({ ...addStudentData, password: e.target.value })}
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                      data-testid="add-student-password"
                    />
                  </div>
                  <div className="md:col-span-3 flex gap-3">
                    <Button type="submit" disabled={addingStudent} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-student-submit">
                      {addingStudent ? 'Adding...' : 'Add Student'}
                    </Button>
                    <Button type="button" variant="outline" className="border-slate-600" onClick={() => setShowAddStudent(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Bulk Import Panel */}
            {showBulkImport && (
              <div className="mb-6 p-6 bg-slate-800/80 border border-slate-700 rounded-xl">
                <h4 className="text-white font-semibold mb-2">Bulk Import Students</h4>
                <p className="text-slate-300 text-sm mb-4">
                  Upload a CSV file with columns: <code className="text-emerald-400 bg-slate-900 px-1 rounded">full_name</code>, <code className="text-emerald-400 bg-slate-900 px-1 rounded">email</code>, <code className="text-emerald-400 bg-slate-900 px-1 rounded">password</code> (optional, defaults to student123)
                </p>
                <div className="flex items-center gap-4 mb-4">
                  <label
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer transition-colors ${
                      bulkUploading
                        ? 'bg-slate-700 text-slate-300 cursor-wait'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    }`}
                    data-testid="bulk-csv-upload"
                  >
                    <Upload className="w-4 h-4" />
                    {bulkUploading ? 'Importing...' : 'Choose CSV File'}
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleBulkUpload}
                      disabled={bulkUploading}
                      className="hidden"
                      data-testid="bulk-csv-input"
                    />
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-slate-600 text-slate-300"
                    onClick={handleDownloadTemplate}
                    data-testid="download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Download Template
                  </Button>
                  <Button type="button" variant="outline" className="border-slate-600" onClick={() => { setShowBulkImport(false); setBulkResult(null); }}>
                    Cancel
                  </Button>
                </div>

                {bulkResult && (
                  <div className="bg-slate-900 rounded-lg p-4 space-y-3" data-testid="bulk-result">
                    <div className="flex gap-6 text-sm">
                      <span className="text-slate-300">Total rows: <span className="text-white font-medium">{bulkResult.total_rows}</span></span>
                      <span className="text-emerald-400">Created: <span className="font-medium">{bulkResult.created}</span></span>
                      {bulkResult.skipped > 0 && (
                        <span className="text-amber-400">Skipped: <span className="font-medium">{bulkResult.skipped}</span></span>
                      )}
                      {bulkResult.errors > 0 && (
                        <span className="text-red-400">Errors: <span className="font-medium">{bulkResult.errors}</span></span>
                      )}
                    </div>

                    {bulkResult.created_students?.length > 0 && (() => {
                      const tenantSlug = branding?.is_white_label ? (localStorage.getItem('tenant_slug') || '') : '';
                      const baseUrl = window.location.origin;
                      const loginLink = tenantSlug ? `${baseUrl}/login?tenant=${tenantSlug}` : `${baseUrl}/login`;
                      return (
                        <div className="mt-3">
                          <p className="text-emerald-400 text-xs font-medium mb-2">Imported students, share these credentials:</p>
                          <p className="text-blue-400 text-xs mb-3">
                            Login page: <span className="underline cursor-pointer" onClick={() => window.open(loginLink, '_blank')} data-testid="bulk-login-url">{loginLink}</span>
                          </p>
                          <div className="space-y-2">
                            {bulkResult.created_students.map((s, i) => (
                              <div key={i} className="bg-slate-800 rounded-lg px-4 py-2.5 border border-slate-700 flex items-center gap-6">
                                <span className="text-white font-medium text-sm min-w-[140px]">{s.full_name}</span>
                                <span className="text-slate-300 font-mono text-xs">{s.email}</span>
                                <span className="text-slate-300 font-mono text-xs">pw: <span className="text-white">{s.password}</span></span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {bulkResult.skipped_details?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-amber-400 text-xs font-medium mb-1">Skipped rows:</p>
                        {bulkResult.skipped_details.map((s, i) => (
                          <p key={i} className="text-slate-300 text-xs">Row {s.row}: {s.email || ''}, {s.reason}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {roster.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-300 font-medium">Student</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-medium">Pathway</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-medium">CRI Score</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-medium">ATS</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roster.map(student => (
                      <RosterRow 
                        key={student.id} 
                        student={student} 
                        onView={(s) => navigate(`/enterprise/student/${s.id}`)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h4 className="text-white font-semibold mb-2">No students found</h4>
                <p className="text-slate-300">Students will appear here once they join your organization</p>
              </div>
            )}
          </GlassCard>
        </TabsContent>

        {/* Organizations Tab (Super Admin Only) */}
        {isSuperAdmin && (
          <TabsContent value="organizations">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">All Organizations</h3>
              <Button onClick={() => setShowCreateOrg(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Create Organization
              </Button>
            </div>

            {organizations.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {organizations.map(org => (
                  <OrgCard 
                    key={org.id} 
                    org={org} 
                    onManage={(o) => navigate(`/enterprise/organizations/${o.id}`)}
                  />
                ))}
              </div>
            ) : (
              <GlassCard className="p-12 text-center">
                <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h4 className="text-white font-semibold mb-2">No organizations yet</h4>
                <p className="text-slate-300 mb-6">Create your first organization to get started</p>
                <Button onClick={() => setShowCreateOrg(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Create Organization
                </Button>
              </GlassCard>
            )}
          </TabsContent>
        )}

        {/* Reports Tab */}
        <TabsContent value="reports">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { id: 'readiness', name: 'Readiness Distribution', icon: BarChart3, desc: 'CRI score distribution across tiers' },
              { id: 'ats', name: 'ATS Trends', icon: TrendingUp, desc: 'Resume score improvements over time' },
              { id: 'pathway', name: 'Pathway Adoption', icon: Target, desc: 'Career path selection breakdown' },
              { id: 'engagement', name: 'Engagement Funnel', icon: Users, desc: 'User activation and retention' },
              { id: 'intervention', name: 'Intervention Summary', icon: AlertCircle, desc: 'At-risk students and actions taken' },
              { id: 'outcomes', name: 'Outcomes Readiness', icon: CheckCircle, desc: 'Placement and completion metrics' }
            ].map(report => (
              <GlassCard key={report.id} className="p-5 hover:border-blue-500/30 transition-all cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <report.icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">{report.name}</h4>
                    <p className="text-slate-300 text-sm">{report.desc}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="flex-1 border-slate-600">
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 border-slate-600">
                    <Download className="w-4 h-4 mr-1" /> Export
                  </Button>
                </div>
              </GlassCard>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Org Modal */}
      <CreateOrgModal 
        isOpen={showCreateOrg} 
        onClose={() => setShowCreateOrg(false)} 
        onCreate={handleCreateOrg}
      />
    </AppLayout>
  );
}
